<template>
    <div class="flex-1 flex items-center justify-center w-full md:w-2/3">
        <svg viewBox="0 0 560 440" class="w-full max-w-sm">
            <!-- Connection lines -->
            <line
                v-for="node in renderedNodes"
                :key="`line-${node.id}`"
                :x1="centerX"
                :y1="centerY"
                :x2="node.x"
                :y2="node.y"
                :stroke="node.lineColor"
                stroke-width="1"
                :opacity="node.opacity * 0.35" />

            <!-- Center node -->
            <circle :cx="centerX" :cy="centerY" r="40" fill="#fee4f0" stroke="#fd8dc1" stroke-width="2" />
            <text :x="centerX" :y="centerY + 5" text-anchor="middle" fill="#df217e" font-size="16" font-weight="600">
                {{ t("onboarding.evaluator.contextKnowledgeGraph.center") }}
            </text>

            <!-- Force-directed outer nodes -->
            <g v-for="node in renderedNodes" :key="node.id" :opacity="node.opacity">
                <circle :cx="node.x" :cy="node.y" :r="node.radius" :fill="node.bgColor" :stroke="node.color" stroke-width="1.5" />
                <text :x="node.x" :y="node.y + node.radius + 14" text-anchor="middle" :fill="node.textColor" font-size="11" font-weight="500">
                    {{ nodeLabels[node.id] }}
                </text>
            </g>
        </svg>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    activeStep?: number;
    // When provided, drives visibility directly by group number (last group is highlighted).
    // Falls back to the accumulating activeStep behavior when omitted.
    activeGroups?: number[];
    // Hides the relationships (people) ring and pulls the week ring inward to fill the gap.
    skipRelationships?: boolean;
    // Pins nodes to their fixed target positions (fade in/out only, no physics/repulsion).
    // Avoids the settling "jump" and stray connector lines when rings appear.
    staticLayout?: boolean;
}>();

const { t } = useI18n();

const centerX = 280;
const centerY = 220;

// Pull the week ring (group 3) into the vacated relationships band when relationships is skipped.
const WEEK_CONTRACT_FACTOR = 0.82;

function targetFor(n: { g: number; tX: number; tY: number }): { x: number; y: number } {
    if (props.skipRelationships && n.g === 3) {
        return { x: centerX + (n.tX - centerX) * WEEK_CONTRACT_FACTOR, y: centerY + (n.tY - centerY) * WEEK_CONTRACT_FACTOR };
    }
    return { x: n.tX, y: n.tY };
}

interface SimNode {
    id: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    targetX: number;
    targetY: number;
    radius: number;
    group: number;
    color: string;
    bgColor: string;
    lineColor: string;
    textColor: string;
    opacity: number;
    targetOpacity: number;
}

// Nodes arranged in radial rings: step 1 closest, each step further out
// Angles scattered across the full circle for variety (not clustered by type)
const nodeDefs = [
    // Step 1 — Personal (pink, inner ring ~110px, angles: 35° 140° 225° 315°)
    { id: "goals", tX: 370, tY: 157, r: 24, g: 1, c: "#ff2891", bg: "#fee4f0", lc: "#fd8dc1", tc: "#c31a78" },
    { id: "patterns", tX: 196, tY: 149, r: 17, g: 1, c: "#ff2891", bg: "#fee4f0", lc: "#fd8dc1", tc: "#c31a78" },
    { id: "style", tX: 202, tY: 298, r: 21, g: 1, c: "#ff2891", bg: "#fee4f0", lc: "#fd8dc1", tc: "#c31a78" },
    { id: "challenges", tX: 358, tY: 298, r: 15, g: 1, c: "#ff2891", bg: "#fee4f0", lc: "#fd8dc1", tc: "#c31a78" },

    // Step 2 — Relationships (orange, second ring ~155px, 6 nodes, angles: 5° 65° 115° 175° 245° 305°)
    { id: "jordan", tX: 434, tY: 207, r: 25, g: 2, c: "#FB945E", bg: "#fef3e0", lc: "#fdb88a", tc: "#d4622c" },
    { id: "sarah", tX: 346, tY: 80, r: 16, g: 2, c: "#FB945E", bg: "#fef3e0", lc: "#fdb88a", tc: "#d4622c" },
    { id: "marcus", tX: 214, tY: 80, r: 20, g: 2, c: "#FB945E", bg: "#fef3e0", lc: "#fdb88a", tc: "#d4622c" },
    { id: "dana", tX: 126, tY: 207, r: 14, g: 2, c: "#FB945E", bg: "#fef3e0", lc: "#fdb88a", tc: "#d4622c" },
    { id: "alex", tX: 214, tY: 352, r: 18, g: 2, c: "#FB945E", bg: "#fef3e0", lc: "#fdb88a", tc: "#d4622c" },
    { id: "taylor", tX: 369, tY: 347, r: 16, g: 2, c: "#FB945E", bg: "#fef3e0", lc: "#fdb88a", tc: "#d4622c" },

    // Step 3 — Your Week (cyan, third ring ~190px, 4 nodes)
    { id: "meetings", tX: 445, tY: 125, r: 22, g: 3, c: "#14C8EB", bg: "#e0f7fd", lc: "#7eddf2", tc: "#0a9bbd" },
    { id: "initiatives", tX: 108, tY: 140, r: 20, g: 3, c: "#14C8EB", bg: "#e0f7fd", lc: "#7eddf2", tc: "#0a9bbd" },
    { id: "role", tX: 290, tY: 382, r: 16, g: 3, c: "#14C8EB", bg: "#e0f7fd", lc: "#7eddf2", tc: "#0a9bbd" },
    { id: "deadlines", tX: 452, tY: 300, r: 23, g: 3, c: "#14C8EB", bg: "#e0f7fd", lc: "#7eddf2", tc: "#0a9bbd" },

    // Step 4 — Organization (purple, outer ring ~210px, 6 nodes)
    { id: "values", tX: 403, tY: 48, r: 19, g: 4, c: "#5852C4", bg: "#eef0fb", lc: "#a9a5de", tc: "#3d38a0" },
    { id: "language", tX: 297, tY: 38, r: 15, g: 4, c: "#5852C4", bg: "#eef0fb", lc: "#a9a5de", tc: "#3d38a0" },
    { id: "strategy", tX: 95, tY: 150, r: 20, g: 4, c: "#5852C4", bg: "#eef0fb", lc: "#a9a5de", tc: "#3d38a0" },
    { id: "norms", tX: 124, tY: 329, r: 17, g: 4, c: "#5852C4", bg: "#eef0fb", lc: "#a9a5de", tc: "#3d38a0" },
    { id: "culture", tX: 150, tY: 378, r: 18, g: 4, c: "#5852C4", bg: "#eef0fb", lc: "#a9a5de", tc: "#3d38a0" },
    { id: "priorities", tX: 478, tY: 290, r: 16, g: 4, c: "#5852C4", bg: "#eef0fb", lc: "#a9a5de", tc: "#3d38a0" },
];

const nodes = reactive<SimNode[]>(
    nodeDefs.map((n) => ({
        id: n.id,
        x: props.staticLayout ? targetFor(n).x : centerX + (Math.random() - 0.5) * 10,
        y: props.staticLayout ? targetFor(n).y : centerY + (Math.random() - 0.5) * 10,
        vx: 0,
        vy: 0,
        targetX: targetFor(n).x,
        targetY: targetFor(n).y,
        radius: n.r,
        group: n.g,
        color: n.c,
        bgColor: n.bg,
        lineColor: n.lc,
        textColor: n.tc,
        opacity: 0,
        targetOpacity: 0,
    })),
);

const nodeLabels = computed<Record<string, string>>(() => ({
    goals: t("onboarding.evaluator.contextKnowledgeGraph.nodes.goals"),
    patterns: t("onboarding.evaluator.contextKnowledgeGraph.nodes.patterns"),
    style: t("onboarding.evaluator.contextKnowledgeGraph.nodes.style"),
    challenges: t("onboarding.evaluator.contextKnowledgeGraph.nodes.challenges"),
    jordan: t("onboarding.evaluator.contextKnowledgeGraph.nodes.jordan"),
    sarah: t("onboarding.evaluator.contextKnowledgeGraph.nodes.sarah"),
    marcus: t("onboarding.evaluator.contextKnowledgeGraph.nodes.marcus"),
    dana: t("onboarding.evaluator.contextKnowledgeGraph.nodes.dana"),
    alex: t("onboarding.evaluator.contextKnowledgeGraph.nodes.alex"),
    taylor: t("onboarding.evaluator.contextKnowledgeGraph.nodes.taylor"),
    meetings: t("onboarding.evaluator.contextKnowledgeGraph.nodes.meetings"),
    initiatives: t("onboarding.evaluator.contextKnowledgeGraph.nodes.initiatives"),
    role: t("onboarding.evaluator.contextKnowledgeGraph.nodes.role"),
    deadlines: t("onboarding.evaluator.contextKnowledgeGraph.nodes.deadlines"),
    values: t("onboarding.evaluator.contextKnowledgeGraph.nodes.values"),
    language: t("onboarding.evaluator.contextKnowledgeGraph.nodes.language"),
    strategy: t("onboarding.evaluator.contextKnowledgeGraph.nodes.strategy"),
    norms: t("onboarding.evaluator.contextKnowledgeGraph.nodes.norms"),
    culture: t("onboarding.evaluator.contextKnowledgeGraph.nodes.culture"),
    priorities: t("onboarding.evaluator.contextKnowledgeGraph.nodes.priorities"),
}));

const renderedNodes = computed(() => nodes.filter((n) => n.opacity > 0.01));

let animFrame: number | null = null;

function simulate() {
    for (const node of nodes) {
        if (node.targetOpacity === 0 && node.opacity < 0.01) {
            continue;
        }

        // Static layout: hold nodes at their fixed targets, animate opacity only.
        if (props.staticLayout) {
            node.x = node.targetX;
            node.y = node.targetY;
            node.opacity += (node.targetOpacity - node.opacity) * 0.06;
            continue;
        }

        // Spring toward target position
        const dx = node.targetX - node.x;
        const dy = node.targetY - node.y;
        node.vx += dx * 0.006;
        node.vy += dy * 0.006;

        // Repulsion from other visible nodes
        for (const other of nodes) {
            if (node === other || (other.targetOpacity === 0 && other.opacity < 0.01)) {
                continue;
            }
            const rx = node.x - other.x;
            const ry = node.y - other.y;
            const dist = Math.sqrt(rx * rx + ry * ry);
            const minDist = node.radius + other.radius + 24;
            if (dist < minDist && dist > 0.1) {
                const force = ((minDist - dist) / dist) * 0.2;
                node.vx += rx * force;
                node.vy += ry * force;
            }
        }

        // Repulsion from center node
        const cx = node.x - centerX;
        const cy = node.y - centerY;
        const cd = Math.sqrt(cx * cx + cy * cy);
        const centerMin = node.radius + 48;
        if (cd < centerMin && cd > 0.1) {
            const cf = ((centerMin - cd) / cd) * 0.5;
            node.vx += cx * cf;
            node.vy += cy * cf;
        }

        // Damping — settle quickly, no ongoing wobble
        node.vx *= 0.85;
        node.vy *= 0.85;

        // Freeze once settled
        if (Math.abs(node.vx) < 0.01 && Math.abs(node.vy) < 0.01 && Math.abs(dx) < 1 && Math.abs(dy) < 1) {
            node.vx = 0;
            node.vy = 0;
            node.x = node.targetX;
            node.y = node.targetY;
        }

        // Update position
        node.x += node.vx;
        node.y += node.vy;

        // Bounds
        node.x = Math.max(40, Math.min(520, node.x));
        node.y = Math.max(node.radius + 5, Math.min(440 - node.radius - 22, node.y));

        // Opacity lerp
        node.opacity += (node.targetOpacity - node.opacity) * 0.06;
    }

    animFrame = requestAnimationFrame(simulate);
}

function updateVisibility() {
    const groups = props.activeGroups ?? Array.from({ length: props.activeStep ?? 0 }, (_, i) => i + 1);
    const highlighted = groups.length ? groups[groups.length - 1] : 0;
    for (const node of nodes) {
        if (props.skipRelationships && node.group === 2) {
            node.targetOpacity = 0;
            continue;
        }
        if (groups.includes(node.group)) {
            node.targetOpacity = node.group === highlighted ? 1 : 0.5;
        } else {
            node.targetOpacity = 0;
        }
    }
}

watch(
    () => [props.activeStep, props.activeGroups],
    () => updateVisibility(),
    { immediate: true },
);

onMounted(() => {
    updateVisibility();
    animFrame = requestAnimationFrame(simulate);
});

onUnmounted(() => {
    if (animFrame !== null) {
        cancelAnimationFrame(animFrame);
    }
});
</script>
